import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useCreateTimesheet } from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { CreateTimesheetParams } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import moment from 'moment';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import TimesheetEmployeeForm, {
  TimesheetEmployeeFormRefType,
  TimesheetEmployeeFormValues,
} from '../components/TimesheetEmployeeForm';

const AddNewTimesheetEmployee = () => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();

  const handleGoBack = () => navigate('/timesheets-employee');

  const timesheetFormRef = useRef<TimesheetEmployeeFormRefType>(null);

  const { run: createTimesheet } = useCreateTimesheet();
  const { user } = useSelector((state: AppState) => state.appStore);
  const [isCreateTsPending, setIsCreateTsPending] = useState(false);

  const [searchParams] = useSearchParams();
  const defaultValuesParams = searchParams.get('defaultValues');
  const defaultValues = defaultValuesParams
    ? JSON.parse(defaultValuesParams)
    : null;

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={messages['timesheet.addNew']}
            title=""
          />
        </div>
      </div>
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.cancel']}
          onClick={handleGoBack}
          className="max-sm:hidden"
        />
        <AppButton
          type="primary"
          name={messages['common.save']}
          onClick={() => timesheetFormRef.current?.submit()}
          loading={isCreateTsPending}
          disabled={isCreateTsPending}
        />
      </div>
    );
  }, [messages, isCreateTsPending]);

  const handleSubmit = async (values: TimesheetEmployeeFormValues) => {
    const params: Partial<CreateTimesheetParams> = {
      averageRate: 0,
      companyRef: '',
      endDate: values.endDate ? moment(values.endDate).toISOString() : null,
      invoicingAddress: {
        address: [],
        city: '',
        county: '',
        postCode: '',
        country: '',
      },
      rates:
        values.rates?.map((rate) => ({
          ...rate,
          rateCode: rate.rateCode?.value ?? '',
          vatDetails: rate.vatDetails?.value ?? '',
          net: rate.net ?? '0',
          total: rate.total ?? '0',
        })) ?? [],
      agencyRef: values.agencyRef?.value ?? null,
      assignmentRef: values.assignmentRef?.value ?? null,
      siteId: values.siteId ?? '',
      startDate: values.startDate
        ? moment(values.startDate).toISOString()
        : null,
      subcontractorRef: user?.subcontractorId?.id ?? '',
      taxPeriodConfig: Number(values.taxPeriod?.value) ?? 1,
      workLocation: {},
      file: '',
      isBureau: true,
    };

    setIsCreateTsPending(true);
    if ((values.file as File)?.name) {
      const res = await dispatch(
        commonActions.uploadFileToS3({ file: values.file as File }),
      ).unwrap();
      params.file = res.url;
    }
    await createTimesheet(params as CreateTimesheetParams)
      .then(() => {
        showMessage({
          type: 'success',
          content: messages['timesheet.createSuccessfully'],
        });
        handleGoBack();
      })
      .finally(() => {
        setIsCreateTsPending(false);
      });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <TimesheetEmployeeForm
        formRef={timesheetFormRef}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        subcontractorId={user?.subcontractorId?.id}
      />
    </AppsContainer>
  );
};

export default AddNewTimesheetEmployee;
